import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Upload,
    Download,
    X,
    FileText,
    AlertCircle,
    CheckCircle,
    Loader2,
    AlertTriangle,
    FileSpreadsheet,
    Copy
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import jobService from '../../../api/services/jobService';
import departmentRoleService from '../../../api/services/departmentRoleService';
import locationService from '../../../api/services/locationService';
import { experienceService, skillService, requirementService } from '../../../api/crm/config/configService';
import { toast } from 'react-hot-toast';

const STATIC_FIELD_VALUES = {
    type: ['full_time', 'part_time', 'contract', 'internship', 'remote'],
    priority: ['high', 'medium', 'low'],
    preferredGender: ['any', 'male', 'female'],
    status: ['draft', 'active', 'closed']
};

const BULK_UPLOAD_FIELD_INSTRUCTIONS = [
    {
        label: 'Job Title',
        column: 'title',
        required: true,
        message: 'Enter the job position title. Use only letters, numbers, spaces, commas, plus signs, slashes, parentheses, and hyphens. Maximum 120 characters.'
    },
    {
        label: 'Department',
        column: 'department',
        required: true,
        message: 'Enter the department name exactly as configured in master data.',
        valuesKey: 'departments'
    },
    {
        label: 'Location',
        column: 'location',
        required: true,
        message: 'Enter the office location name exactly as configured in active locations.',
        valuesKey: 'locations'
    },
    {
        label: 'Job Type',
        column: 'type',
        required: true,
        message: 'Enter the employment type using one of the exact backend values below.',
        valuesKey: 'type'
    },
    {
        label: 'Experience Level',
        column: 'experience',
        required: true,
        message: 'Enter the experience level name exactly as shown in Configure Management.',
        valuesKey: 'experienceLevels'
    },
    {
        label: 'Preferred Gender',
        column: 'preferredGender',
        required: false,
        message: 'Enter the preferred gender for applicants. If left empty, the system uses any.',
        valuesKey: 'preferredGender'
    },
    {
        label: 'Salary Range (Min)',
        column: 'salaryMin',
        required: false,
        message: 'Enter the minimum salary as a number only. Example: 800000'
    },
    {
        label: 'Salary Range (Max)',
        column: 'salaryMax',
        required: false,
        message: 'Enter the maximum salary as a number only. If both min and max are provided, max must be greater than min.'
    },
    {
        label: 'Application Deadline',
        column: 'deadline',
        required: true,
        message: 'Enter the last date to apply in YYYY-MM-DD format. Example: 2026-12-31'
    },
    {
        label: 'Status',
        column: 'status',
        required: true,
        message: 'Enter the job status using one of the exact backend values below.',
        valuesKey: 'status'
    },
    {
        label: 'Priority',
        column: 'priority',
        required: true,
        message: 'Enter the hiring priority using one of the exact backend values below.',
        valuesKey: 'priority'
    },
    {
        label: 'Job Description',
        column: 'description',
        required: true,
        message: 'Enter the full job description. Minimum 10 characters are required.'
    },
    {
        label: 'Add Requirements',
        column: 'requirements',
        required: false,
        message: 'Enter multiple requirements in one cell separated by semicolon (;). Example: React;Node.js;AWS. You can also add custom values.',
        valuesKey: 'requirements'
    },
    {
        label: 'Add Skills',
        column: 'skills',
        required: false,
        message: 'Enter multiple skills in one cell separated by semicolon (;). Example: JavaScript;TypeScript;Redux. You can also add custom values.',
        valuesKey: 'skills'
    }
];

const normalizeDepartmentNames = (departmentsData = []) => {
    const getTimestamp = (dept) => {
        if (!dept || typeof dept === 'string') return null;
        const dateValue = dept.createdAt || dept.updatedAt || dept.created_at || dept.updated_at;
        if (!dateValue) return null;
        const timestamp = new Date(dateValue).getTime();
        return Number.isNaN(timestamp) ? null : timestamp;
    };

    const hasTimestamp = departmentsData.some((dept) => getTimestamp(dept) !== null);
    const orderedDepartments = hasTimestamp
        ? [...departmentsData].sort((a, b) => (getTimestamp(b) ?? 0) - (getTimestamp(a) ?? 0))
        : [...departmentsData].reverse();

    return orderedDepartments
        .map((dept) => {
            if (typeof dept === 'string') return dept;
            return dept?.name || dept?.departmentName || dept?.title || dept?.department || '';
        })
        .filter(Boolean);
};

/**
 * JobBulkUploadModal - Modal for bulk uploading jobs from CSV
 */
const JobBulkUploadModal = ({ isOpen, onClose, onSuccess }) => {
    const [step, setStep] = useState('upload'); // 'upload', 'preview', 'uploading', 'result'
    const [file, setFile] = useState(null);
    const [parsedJobs, setParsedJobs] = useState([]);
    const [validationResult, setValidationResult] = useState(null);
    const [uploadResult, setUploadResult] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [skipDuplicates, setSkipDuplicates] = useState(true);
    const fileInputRef = useRef(null);

    const { data: departments = [], isLoading: isLoadingDepartments } = useQuery({
        queryKey: ['bulkUploadDepartments'],
        queryFn: async () => {
            const result = await departmentRoleService.getDepartments();
            let departmentsData = [];

            if (Array.isArray(result)) {
                departmentsData = result;
            } else if (result?.data) {
                departmentsData = Array.isArray(result.data) ? result.data : result.data.departments || [];
            } else if (result?.responseCode === 200) {
                departmentsData = result.data?.departments || result.data || [];
            }

            return normalizeDepartmentNames(departmentsData);
        },
        enabled: isOpen,
        staleTime: 5 * 60 * 1000
    });

    const { data: locations = [], isLoading: isLoadingLocations } = useQuery({
        queryKey: ['bulkUploadLocations'],
        queryFn: async () => {
            const result = await locationService.getActiveLocations();
            if (result.success) {
                return (result.data || []).map((loc) => loc.name || loc.label || loc.value).filter(Boolean);
            }
            return [];
        },
        enabled: isOpen,
        staleTime: 5 * 60 * 1000
    });

    const { data: experienceLevels = [], isLoading: isLoadingExperience } = useQuery({
        queryKey: ['bulkUploadExperienceLevels'],
        queryFn: async () => {
            const result = await experienceService.getAll({ isActive: true });
            if (result && (result.success || result.responseCode === 200)) {
                return (result.data || []).map((item) => item.name).filter(Boolean);
            }
            return [];
        },
        enabled: isOpen,
        staleTime: 5 * 60 * 1000
    });

    const { data: requirements = [], isLoading: isLoadingRequirements } = useQuery({
        queryKey: ['bulkUploadRequirements'],
        queryFn: async () => {
            const result = await requirementService.getAll({ isActive: true });
            if (result && (result.success || result.responseCode === 200)) {
                return (result.data || []).map((item) => item.name).filter(Boolean);
            }
            return [];
        },
        enabled: isOpen,
        staleTime: 5 * 60 * 1000
    });

    const { data: skills = [], isLoading: isLoadingSkills } = useQuery({
        queryKey: ['bulkUploadSkills'],
        queryFn: async () => {
            const result = await skillService.getAll({ isActive: true });
            if (result && (result.success || result.responseCode === 200)) {
                return (result.data || []).map((item) => item.name).filter(Boolean);
            }
            return [];
        },
        enabled: isOpen,
        staleTime: 5 * 60 * 1000
    });

    const fieldValueMap = useMemo(() => ({
        departments,
        locations,
        experienceLevels,
        requirements,
        skills,
        ...STATIC_FIELD_VALUES
    }), [departments, locations, experienceLevels, requirements, skills]);

    const isLoadingFieldValues = isLoadingDepartments || isLoadingLocations || isLoadingExperience || isLoadingRequirements || isLoadingSkills;

    const getFieldValues = (valuesKey) => {
        if (!valuesKey) return [];
        return fieldValueMap[valuesKey] || [];
    };

    // Reset state when modal closes
    const handleClose = useCallback(() => {
        setStep('upload');
        setFile(null);
        setParsedJobs([]);
        setValidationResult(null);
        setUploadResult(null);
        setIsUploading(false);
        setIsValidating(false);
        onClose();
    }, [onClose]);

    // Handle file selection
    const handleFileChange = async (e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        // Validate file type
        const validTypes = ['text/csv', 'application/vnd.ms-excel', '.csv'];
        const isValidType = validTypes.some(type =>
            selectedFile.type === type || selectedFile.name.endsWith('.csv')
        );

        if (!isValidType) {
            toast.error('Please upload a CSV file');
            return;
        }

        // Validate file size (max 5MB)
        if (selectedFile.size > 5 * 1024 * 1024) {
            toast.error('File size must be less than 5MB');
            return;
        }

        setFile(selectedFile);

        // Read and parse the file
        const reader = new FileReader();
        reader.onload = async (event) => {
            const csvContent = event.target?.result;
            if (typeof csvContent !== 'string') return;

            const result = jobService.parseCSV(csvContent);
            if (!result.success) {
                toast.error(result.error || 'Failed to parse CSV');
                setFile(null);
                return;
            }

            if (result.jobs.length === 0) {
                toast.error('No valid job data found in CSV');
                setFile(null);
                return;
            }

            if (result.jobs.length > 500) {
                toast.error('Maximum 500 jobs per upload. Please split your file.');
                setFile(null);
                return;
            }

            setParsedJobs(result.jobs);
            setStep('preview');

            // Auto-validate
            await validateJobs(result.jobs);
        };

        reader.onerror = () => {
            toast.error('Failed to read file');
            setFile(null);
        };

        reader.readAsText(selectedFile);
    };

    // Validate parsed jobs
    const validateJobs = async (jobs) => {
        setIsValidating(true);
        try {
            const result = await jobService.validateBulkUpload(jobs);
            setValidationResult(result);
        } catch (error) {
            toast.error('Validation failed: ' + (error.message || 'Unknown error'));
        } finally {
            setIsValidating(false);
        }
    };

    // Download template
    const handleDownloadTemplate = async () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;

        const filename = `job_bulk_upload_template_${day}-${month}-${year}_${String(hours).padStart(2, '0')}-${minutes}_${ampm}.csv`;
        const result = await jobService.downloadBulkUploadTemplate(filename);
        if (!result.success) {
            toast.error(result.error || 'Failed to download template');
        } else {
            toast.success('Template downloaded');
        }
    };

    // Handle drag and drop
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const droppedFile = e.dataTransfer?.files?.[0];
        if (droppedFile) {
            const event = { target: { files: [droppedFile] } };
            handleFileChange(event);
        }
    };

    // Perform the actual upload
    const handleUpload = async () => {
        if (parsedJobs.length === 0) return;

        setIsUploading(true);
        setStep('uploading');

        try {
            const result = await jobService.bulkUploadJobs(parsedJobs, { skipDuplicates });
            setUploadResult(result);
            setStep('result');

            if (result.success) {
                const created = result.data?.created || 0;
                const updated = result.data?.updated || 0;
                const parts = [];
                if (created > 0) parts.push(`${created} created`);
                if (updated > 0) parts.push(`${updated} updated`);
                toast.success(parts.length > 0 ? `Successfully ${parts.join(', ')} jobs` : 'Upload complete');
                if (onSuccess) onSuccess();
            } else {
                toast.error(result.error || 'Upload failed');
            }
        } catch (error) {
            toast.error(error.message || 'Upload failed');
            setUploadResult({
                success: false,
                error: error.message || 'Upload failed'
            });
            setStep('result');
        } finally {
            setIsUploading(false);
        }
    };

    // Go back to previous step
    const handleBack = () => {
        if (step === 'preview') {
            setStep('upload');
            setFile(null);
            setParsedJobs([]);
            setValidationResult(null);
        } else if (step === 'result') {
            handleClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-agrosaf-green/10 rounded-lg">
                            <Upload className="h-5 w-5 text-agrosaf-green" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Bulk Upload Jobs</h2>
                            <p className="text-sm text-gray-500">
                                {step === 'upload' && 'Upload a CSV file with job data'}
                                {step === 'preview' && (() => {
                                    const errorCount = validationResult?.data?.failed || 0;
                                    const duplicateCount = validationResult?.data?.duplicates?.length || 0;
                                    const readyCount = Math.max(0, parsedJobs.length - errorCount - duplicateCount);
                                    if (readyCount > 0) {
                                        return `${readyCount} of ${parsedJobs.length} jobs ready for upload`;
                                    } else {
                                        return `${parsedJobs.length} jobs parsed, none ready for upload`;
                                    }
                                })()}
                                {step === 'uploading' && 'Uploading jobs...'}
                                {step === 'result' && 'Upload complete'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Upload Step */}
                    {step === 'upload' && (
                        <div className="space-y-6">
                            {/* Drag & Drop Zone */}
                            <div
                                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-agrosaf-green hover:bg-agrosaf-green/5 transition-all cursor-pointer"
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                                <FileSpreadsheet className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-lg font-medium text-gray-700 mb-2">
                                    Drag & drop your CSV file here
                                </p>
                                <p className="text-sm text-gray-500 mb-4">
                                    or click to browse (Max 500 jobs, 5MB)
                                </p>
                                <Button variant="outline" icon={<Upload className="h-4 w-4" />}>
                                    Select File
                                </Button>
                            </div>

                            {/* Template Download */}
                            <div className="bg-blue-50 rounded-xl p-4 flex items-start space-x-4">
                                <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                                    <FileText className="h-5 w-5 text-blue-600" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-medium text-blue-900 mb-1">
                                        Need a template?
                                    </h4>
                                    <p className="text-sm text-blue-700 mb-3">
                                        Download our CSV template with all the required fields and sample data.
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        icon={<Download className="h-4 w-4" />}
                                        onClick={handleDownloadTemplate}
                                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                                    >
                                        Download Template
                                    </Button>
                                </div>
                            </div>

                            {/* Field Instructions */}
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-1">CSV Field Instructions</h4>
                                <p className="text-xs text-gray-500 mb-4">
                                    Use the same column names as the downloaded template. Enter values exactly as mentioned below.
                                </p>

                                <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                                    {BULK_UPLOAD_FIELD_INSTRUCTIONS.map((field) => {
                                        const values = getFieldValues(field.valuesKey);
                                        const showValues = Boolean(field.valuesKey);

                                        return (
                                            <div key={field.column} className="bg-white border border-gray-200 rounded-lg p-4">
                                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                                    <h5 className="text-sm font-semibold text-gray-900">{field.label}</h5>
                                                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${field.required ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                                                        {field.required ? 'Mandatory' : 'Optional'}
                                                    </span>
                                                    <span className="text-[11px] text-gray-500 font-mono">CSV column: {field.column}</span>
                                                </div>

                                                <p className="text-sm text-gray-700 mb-3">{field.message}</p>

                                                {showValues && (
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                                                            Accepted Values
                                                        </p>
                                                        {isLoadingFieldValues ? (
                                                            <div className="flex items-center text-xs text-gray-500">
                                                                <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                                                                Loading accepted values...
                                                            </div>
                                                        ) : values.length > 0 ? (
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {values.map((value) => (
                                                                    <code
                                                                        key={`${field.column}-${value}`}
                                                                        className="px-2 py-0.5 bg-gray-100 border border-gray-200 rounded text-xs font-mono text-gray-800"
                                                                    >
                                                                        {value}
                                                                    </code>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-xs text-gray-500">No configured values found.</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Preview Step */}
                    {step === 'preview' && (
                        <div className="space-y-6">
                            {/* File Info */}
                            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center space-x-3">
                                    <FileSpreadsheet className="h-8 w-8 text-agrosaf-green" />
                                    <div>
                                        <p className="font-medium text-gray-900">{file?.name}</p>
                                        <p className="text-sm text-gray-500">{parsedJobs.length} jobs parsed</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setFile(null);
                                        setParsedJobs([]);
                                        setValidationResult(null);
                                        setStep('upload');
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Validation Status */}
                            {isValidating ? (
                                <div className="flex items-center justify-center p-8">
                                    <Loader2 className="h-8 w-8 animate-spin text-agrosaf-green" />
                                    <span className="ml-3 text-gray-600">Validating jobs...</span>
                                </div>
                            ) : validationResult && (() => {
                                const totalJobs = parsedJobs.length;
                                const errorCount = validationResult.data?.failed || 0;
                                const duplicateCount = validationResult.data?.duplicates?.length || 0;
                                const readyToUpload = Math.max(0, totalJobs - errorCount - duplicateCount);

                                return (
                                    <div className="space-y-4">
                                        {/* Summary */}
                                        <div className="grid grid-cols-4 gap-3">
                                            <div className="bg-blue-50 rounded-lg p-3 text-center">
                                                <p className="text-xl font-bold text-blue-700">
                                                    {totalJobs}
                                                </p>
                                                <p className="text-xs text-blue-600">Total Parsed</p>
                                            </div>
                                            <div className="bg-red-50 rounded-lg p-3 text-center">
                                                <AlertCircle className="h-5 w-5 text-red-600 mx-auto mb-1" />
                                                <p className="text-xl font-bold text-red-700">
                                                    {errorCount}
                                                </p>
                                                <p className="text-xs text-red-600">Errors</p>
                                            </div>
                                            <div className="bg-yellow-50 rounded-lg p-3 text-center">
                                                <AlertTriangle className="h-5 w-5 text-yellow-600 mx-auto mb-1" />
                                                <p className="text-xl font-bold text-yellow-700">
                                                    {duplicateCount}
                                                </p>
                                                <p className="text-xs text-yellow-600">Duplicates</p>
                                            </div>
                                            <div className={`rounded-lg p-3 text-center ${readyToUpload > 0 ? 'bg-green-50' : 'bg-gray-50'}`}>
                                                <CheckCircle className={`h-5 w-5 mx-auto mb-1 ${readyToUpload > 0 ? 'text-green-600' : 'text-gray-400'}`} />
                                                <p className={`text-xl font-bold ${readyToUpload > 0 ? 'text-green-700' : 'text-gray-500'}`}>
                                                    {readyToUpload}
                                                </p>
                                                <p className={`text-xs ${readyToUpload > 0 ? 'text-green-600' : 'text-gray-500'}`}>Ready to Upload</p>
                                            </div>
                                        </div>

                                        {/* Errors */}
                                        {validationResult.data?.errors?.length > 0 && (
                                            <div className="bg-red-50 rounded-lg p-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h4 className="text-sm font-medium text-red-800">Validation Errors</h4>
                                                    <button
                                                        onClick={() => {
                                                            const allErrors = validationResult.data.errors.join('\n');
                                                            navigator.clipboard.writeText(allErrors);
                                                            toast.success(`Copied ${validationResult.data.errors.length} errors to clipboard`);
                                                        }}
                                                        className="flex items-center space-x-1 text-xs text-red-600 hover:text-red-800 bg-red-100 hover:bg-red-200 px-2 py-1 rounded transition-colors"
                                                        title="Copy all errors"
                                                    >
                                                        <Copy className="h-3 w-3" />
                                                        <span>Copy All</span>
                                                    </button>
                                                </div>
                                                <div className="max-h-32 overflow-y-auto space-y-2">
                                                    {validationResult.data.errors.slice(0, 10).map((error, idx) => (
                                                        <div key={idx} className="text-sm text-red-700 bg-red-100/50 p-2 rounded border border-red-200">
                                                            <span className="font-bold mr-1">Row {error.row}:</span>
                                                            {Array.isArray(error.errors) ? error.errors.join(', ') : (error.error || String(error))}
                                                        </div>
                                                    ))}
                                                    {validationResult.data.errors.length > 10 && (
                                                        <p className="text-sm text-red-600 font-medium px-2">
                                                            ... and {validationResult.data.errors.length - 10} more errors
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Skip Duplicates Option */}
                                        <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                                            <input
                                                type="checkbox"
                                                id="skipDuplicates"
                                                checked={skipDuplicates}
                                                onChange={(e) => setSkipDuplicates(e.target.checked)}
                                                className="h-4 w-4 text-agrosaf-green rounded border-gray-300 focus:ring-agrosaf-green"
                                            />
                                            <label htmlFor="skipDuplicates" className="text-sm text-gray-700">
                                                Skip duplicate jobs and continue with upload
                                            </label>
                                        </div>

                                        {/* Preview Table */}
                                        <div className="border rounded-lg overflow-hidden">
                                            <div className="bg-gray-50 px-4 py-2 border-b">
                                                <h4 className="text-sm font-medium text-gray-900">Preview (First 5 jobs)</h4>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Dept</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Gender</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-200">
                                                        {parsedJobs.slice(0, 5).map((job, idx) => (
                                                            <tr key={idx}>
                                                                <td className="px-4 py-2 text-sm text-gray-900 font-medium">{job.title}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500">{job.department}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500">{job.location}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500">{job.type}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500 capitalize">{job.status || '-'}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500 capitalize">{job.priority || '-'}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500 capitalize">{job.preferredGender || 'Any'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* Uploading Step */}
                    {step === 'uploading' && (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="h-12 w-12 animate-spin text-agrosaf-green mb-4" />
                            <p className="text-lg font-medium text-gray-900 mb-2">Uploading Jobs...</p>
                            <p className="text-sm text-gray-500">Please wait while we process your data</p>
                        </div>
                    )}

                    {/* Result Step */}
                    {step === 'result' && uploadResult && (
                        <div className="space-y-6">
                            <div className={`text-center py-8 rounded-xl ${uploadResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                                {uploadResult.success ? (
                                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                                ) : (
                                    <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                                )}
                                <h3 className={`text-xl font-semibold mb-2 ${uploadResult.success ? 'text-green-800' : 'text-red-800'}`}>
                                    {uploadResult.success ? 'Upload Successful!' : 'Upload Failed'}
                                </h3>
                                <p className={`text-sm ${uploadResult.success ? 'text-green-600' : 'text-red-600'}`}>
                                    {uploadResult.message || uploadResult.error}
                                </p>
                            </div>

                            {uploadResult.data && (
                                <div className="grid grid-cols-4 gap-3">
                                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-gray-700">{uploadResult.data.total || 0}</p>
                                        <p className="text-sm text-gray-500">Total</p>
                                    </div>
                                    <div className="bg-green-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-green-700">{uploadResult.data.created || 0}</p>
                                        <p className="text-sm text-green-600">Created</p>
                                    </div>
                                    <div className="bg-red-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-red-700">{uploadResult.data.failed || 0}</p>
                                        <p className="text-sm text-red-600">Failed</p>
                                    </div>
                                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-yellow-700">{uploadResult.data.skipped || 0}</p>
                                        <p className="text-sm text-yellow-600">Skipped</p>
                                    </div>
                                </div>
                            )}

                            {/* Detailed error list when upload has failures */}
                            {(uploadResult.data?.failed > 0 || !uploadResult.success) && (
                                <div className="mt-4 max-h-48 overflow-y-auto bg-red-50 rounded-lg p-4 border border-red-100">
                                    <h4 className="text-sm font-semibold text-red-800 mb-2 flex items-center">
                                        <AlertCircle className="h-4 w-4 mr-2" />
                                        Upload Details & Errors
                                    </h4>
                                    {uploadResult.error && !uploadResult.data?.errors?.length && (
                                        <p className="text-sm text-red-700 mb-2">{uploadResult.error}</p>
                                    )}
                                    {uploadResult.data?.errors?.length > 0 ? (
                                        <ul className="space-y-2">
                                            {uploadResult.data.errors.map((err, idx) => (
                                                <li key={idx} className="text-sm text-red-700 bg-white/50 p-2 rounded border border-red-200">
                                                    <span className="font-bold mr-1">Row {err.row}:</span>
                                                    {Array.isArray(err.errors) ? err.errors.join(', ') : (err.error || String(err))}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : validationResult?.data?.errors?.length > 0 && uploadResult.data?.failed > 0 ? (
                                        <ul className="space-y-2">
                                            {validationResult.data.errors.map((err, idx) => (
                                                <li key={idx} className="text-sm text-red-700 bg-white/50 p-2 rounded border border-red-200">
                                                    <span className="font-bold mr-1">Row {err.row}:</span>
                                                    {Array.isArray(err.errors) ? err.errors.join(', ') : (err.error || String(err))}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-red-600 italic">No specific row errors were returned, but some jobs failed to upload.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center space-x-3">
                        {step === 'preview' && (
                            <Button variant="outline" onClick={handleBack}>
                                Back
                            </Button>
                        )}
                        <Button variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                    </div>

                    {step === 'preview' && (() => {
                        const errorCount = validationResult?.data?.failed || 0;
                        const validJobsCount = Math.max(0, parsedJobs.length - errorCount - (skipDuplicates ? (validationResult?.data?.duplicates?.length || 0) : 0));
                        const hasNoValidJobs = validJobsCount <= 0;

                        return (
                            <div className="flex items-center space-x-3">
                                {hasNoValidJobs && (
                                    <span className="text-sm text-red-600">No valid jobs to upload</span>
                                )}
                                <Button
                                    variant="primary"
                                    icon={isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    onClick={handleUpload}
                                    disabled={isUploading || isValidating || hasNoValidJobs}
                                    className="bg-agrosaf-green hover:bg-agrosaf-darkGreen disabled:bg-gray-400"
                                >
                                    {isUploading ? 'Uploading...' : `Upload ${validJobsCount} Jobs`}
                                </Button>
                            </div>
                        );
                    })()}

                    {step === 'result' && (
                        <Button
                            variant="primary"
                            onClick={handleClose}
                            className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                        >
                            Done
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default JobBulkUploadModal;
